import React, { useMemo } from 'react';
import './LiveArea.css';

/**
 * LiveArea — shown when AddressCheck returns status "live".
 * Tells the user we deliver to them and sends them to the app (no web ordering yet).
 *
 * Props
 *  - address          full address string from googleAddress
 *  - onChangeAddress  go back to AddressCheck
 *  - appStoreUrl      iOS App Store link
 *  - playStoreUrl     Google Play link
 *  - qr               optional QR shown on desktop: an image URL (string) or a React node
 *                     e.g. <QRCodeSVG value={downloadUrl} /> from qrcode.react
 */

const getPlatform = () => {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';
  const isIPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  if (/iPhone|iPad|iPod/i.test(ua) || isIPadOS) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'other';
};

const AppleIcon = () => (
  <svg viewBox='0 0 24 24' aria-hidden='true'>
    <path d='M16.4 12.6c0-2.4 2-3.6 2.1-3.6-1.1-1.7-2.9-1.9-3.5-1.9-1.5-.2-2.9.9-3.7.9-.8 0-1.9-.9-3.2-.8-1.6 0-3.1 1-4 2.4-1.7 3-.4 7.3 1.2 9.7.8 1.2 1.8 2.5 3 2.4 1.2 0 1.7-.8 3.1-.8 1.5 0 1.9.8 3.2.8 1.3 0 2.2-1.2 3-2.4.9-1.4 1.3-2.7 1.3-2.8-.1 0-2.5-1-2.5-3.9zM14 5.5c.7-.8 1.1-1.9 1-3-1 0-2.1.7-2.8 1.5-.6.7-1.2 1.8-1 2.9 1.1.1 2.1-.6 2.8-1.4z' />
  </svg>
);

const PlayIcon = () => (
  <svg viewBox='0 0 24 24' aria-hidden='true'>
    <path d='M4 2.8v18.4c0 .4.2.7.5.8l10-10-10-10c-.3.1-.5.4-.5.8zm12 7.8L5.9 2.1l12.3 7.1-2.2 1.4zm0 2.8 2.2 1.4-12.3 7.1L16 13.4zM19.3 10l2.3 1.3c.6.4.6 1.1 0 1.5L19.3 14 16.9 12l2.4-2z' />
  </svg>
);

export default function LiveArea({ address, onChangeAddress, appStoreUrl, playStoreUrl, qr }) {
  const platform = useMemo(getPlatform, []);

  const ios = (
    <a key='ios' className='la-store' href={appStoreUrl} target='_blank' rel='noopener noreferrer'>
      <AppleIcon />
      <span>
        <small>Download on the</small>
        <strong>App Store</strong>
      </span>
    </a>
  );
  const android = (
    <a
      key='android'
      className='la-store'
      href={playStoreUrl}
      target='_blank'
      rel='noopener noreferrer'
    >
      <PlayIcon />
      <span>
        <small>Get it on</small>
        <strong>Google Play</strong>
      </span>
    </a>
  );

  // On a phone, show that phone's store first and make it the only big button
  const stores = platform === 'ios' ? [ios] : platform === 'android' ? [android] : [ios, android];
  const otherStore = platform === 'ios' ? android : platform === 'android' ? ios : null;

  return (
    <section className='la'>
      <div className='la-card'>
        <div className='la-msg'>
          {address && (
            <div className='la-where'>
              <span className='la-whereText'>
                <svg className='la-pin' viewBox='0 0 24 24' aria-hidden='true'>
                  <path d='M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z' />
                </svg>
                <b>{address}</b>
              </span>
              {onChangeAddress && (
                <button type='button' className='la-link' onClick={onChangeAddress}>
                  Change
                </button>
              )}
            </div>
          )}

          <span className='la-status'>
            <i aria-hidden='true' />
            Delivering in your area
          </span>

          <h1 className='la-title'>
            Good news. <em>We deliver to you.</em>
          </h1>
          <p className='la-sub'>
            Restaurants near you are ready to take your order. Ordering happens in the SingleCombo
            app for now, so grab it to place your first order.
          </p>

          <div className={`la-stores${stores.length === 1 ? ' la-stores--single' : ''}`}>
            {stores}
          </div>

          {otherStore && (
            <p className='la-alt'>
              On another device?{' '}
              <a
                href={platform === 'ios' ? playStoreUrl : appStoreUrl}
                target='_blank'
                rel='noopener noreferrer'
              >
                {platform === 'ios' ? 'Get it on Google Play' : 'Get it on the App Store'}
              </a>
            </p>
          )}

          <ol className='la-steps'>
            <li>
              <span>
                <b>Download the app</b>Free on iPhone and Android.
              </span>
            </li>
            <li>
              <span>
                <b>Enter this address</b>See restaurants that deliver here.
              </span>
            </li>
            <li>
              <span>
                <b>Place your order</b>Track it right to your door.
              </span>
            </li>
          </ol>

          <div className='la-webnote'>
            <svg viewBox='0 0 24 24' aria-hidden='true'>
              <rect x='3' y='4' width='18' height='13' rx='2' />
              <path d='M8 21h8M12 17v4' />
            </svg>
            <span>
              <b>Ordering on the web is coming soon.</b> Until then, the app is the fastest way to
              order.
            </span>
          </div>
        </div>

        <aside className='la-visual' aria-hidden='true'>
          <div className='la-phone'>
            <div className='la-screen'>
              <div className='la-notch' />
              <div className='la-app'>
                <div className='la-appHi'>Hungry?</div>
                <div className='la-appAddr'>Restaurants near you</div>
                <div className='la-appTile'>
                  <span>Neighborhood Favorites</span>
                </div>
                <div className='la-appRow'>
                  <div />
                  <div />
                </div>
                <div className='la-appRow'>
                  <div />
                  <div />
                </div>
                <div className='la-appBtn'>Order now</div>
              </div>
            </div>
          </div>

          {qr && (
            <div className='la-qr'>
              {typeof qr === 'string' ? <img src={qr} alt='' /> : qr}
              <p>Scan to get the app</p>
            </div>
          )}
        </aside>
      </div>
    </section>
  );
}
